import "./ReservationSidebarInvoice.scss"

import { FC, useCallback, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import cn from "classnames"
import { Dayjs } from "utils/dayjs"

import { ErpInvoiceStatus, OutgoingInvoiceCreditType, OutgoingInvoiceStatus, OutgoingInvoiceType, ReservationOutgoingInvoice } from "models/OutgoingInvoice"
import OutgoingInvoiceControls from "../OutgoingInvoiceControls/OutgoingInvoiceControls"
import { Grid, GridItem } from "components/Grid"
import Text from "components/Text/Text"
import Spoiler from "components/Spoiler/Spoiler"
import UserService from "services/UserService"
import List, { IListItem, ListItemType } from "components/List/List"
import toastMessages from "stores/toastMessages"
import AssetService from "services/AssetService"
import ErpOrderNumber from "components/ErpInvoice/ErpOrderNumber"
import ErpInvoiceNumber from "components/ErpInvoice/ErpInvoiceNumber"
import ErpInvoiceDate from "components/ErpInvoice/ErpInvoiceDate"
import CustomerNumber from "components/CustomerNumber/CustomerNumber"
import CustomerNumberBlockedModal from "components/CustomerNumber/CustomerNumberBlockedModal"
import { usePermissions } from "stores/permissions"
import { Currency, UserPermission } from "config/constants"
import AdHocInvoiceIcon from "components/AdHocInvoiceIcon/AdHocInvoiceIcon"
import { Flex, FlexItem } from "components/Flex"
import useExchangeRates from "hooks/useExchangeRates"
import useTenantCurrency from "hooks/useTenantCurrency"
import { applyExchangeRate } from "stores/exchangeRates"
import NoValue from "components/NoValue/NoValue"
import Checkbox from "components/Checkbox/Checkbox"
import { Guid, IApiLink } from "@/types"

interface IReservationSidebarInvoiceProps {
    reservationId: Guid
    reservationInvoice: ReservationOutgoingInvoice
    parentInvoicesErp?: { erpNumber?: string, erpNumberStatus?: ErpInvoiceStatus }[]
    onNoteCreated(): Promise<void>
    onInvoiceBlocked(): Promise<void>
    onSelectionToggle?(): void
    refreshInvoices(): Promise<any>
    isInvoiceERPInterfaceConnected: boolean
    isSelected?: boolean
    currency: Currency | null
    exchangeRateDate: Dayjs | null
    disabled?: boolean
    onInvoiceResubmitted?(invoiceId: Guid): void
    resubmitInvoiceLink?: IApiLink
}

const SYNC_REFRESH_DELAY_MS = 500

const ReservationSidebarInvoice: FC<IReservationSidebarInvoiceProps> = ({
    reservationId,
    reservationInvoice,
    parentInvoicesErp,
    onNoteCreated,
    onInvoiceBlocked,
    onSelectionToggle,
    refreshInvoices,
    isInvoiceERPInterfaceConnected,
    isSelected = false,
    currency,
    exchangeRateDate,
    disabled,
    onInvoiceResubmitted,
    resubmitInvoiceLink
}) => {
    const { t } = useTranslation()
    const { hasPermission } = usePermissions()
    const [showCustomerNumberBlockedModal, setShowCustomerNumberBlockedModal] = useState(false)
    const tenantCurrency = useTenantCurrency()
    const { getExchangeRate } = useExchangeRates()

    const {
        amount,
        type,
        creditType,
        erpOrderNumber,
        erpOrderNumberStatus,
        erpOrderNumberMessage,
        erpInvoiceNumber,
        erpInvoiceNumberStatus,
        erpInvoiceDate,
        erpInvoiceDateStatus,
        createdBy: createdById,
        internalComment,
        customer,
        costUnit,
    } = reservationInvoice

    const [createdBy, setCreatedBy] = useState<string>()
    const [expanded, setExpanded] = useState(false)

    const [isSendToErpPending, setIsSendToErpPending] = useState(false)
    const [isSyncPending, setIsSyncPending] = useState(false)
    const [syncRefreshTimeoutId, setSyncRefreshTimeoutId] = useState<NodeJS.Timeout | null>(null)

    const renderInvoiceAmount = () => {
        const isRed = type === OutgoingInvoiceType.fullCreditNote
            || type === OutgoingInvoiceType.partialCreditNote
            || type === OutgoingInvoiceType.collectiveCreditNote

        const exchangeRate = currency && exchangeRateDate
            ? getExchangeRate({
                currency,
                date: exchangeRateDate,
            })
            : null

        return (
            <>
                <Text type="h5" className={isRed ? "color-red-500" : undefined}>
                    {t('common.amountWithCurrency', {
                        count: amount,
                        currency,
                    })}
                </Text>
                {!!exchangeRate && !!tenantCurrency &&
                    <Text type="pSmallest" className={isRed ? "color-red-500" : "color-grey-500"}>
                        {t('common.amountWithCurrency', {
                            count: applyExchangeRate(amount, exchangeRate),
                            currency: tenantCurrency,
                        })}
                    </Text>
                }
            </>
        )
    }

    const renderCreditedTag = () => {
        if (!creditType) return null
        return <Text type="pSmallest" className="px-1 font-bold color-white round-sm d-inline-block bg-red-400">
            {creditType === OutgoingInvoiceCreditType.canceled && t("requestDetails.canceled")}
            {creditType === OutgoingInvoiceCreditType.partiallyCredited && t("requestDetails.credited")}
        </Text>
    }

    const fetchCreator = useCallback(async () => {
        const emails = await UserService.getUserEmailsByIds([createdById])
        setCreatedBy(emails[createdById] || "")
    }, [createdById])

    useEffect(() => {
        if (!expanded || createdBy) return
        fetchCreator()
    }, [fetchCreator, expanded, createdBy])

    function refreshInvoicesWithTimeout() {
        setSyncRefreshTimeoutId(
            // as sync operation is async on backend, we have to wait before refetching invoices
            setTimeout(async () => {
                await refreshInvoices()
                setIsSendToErpPending(false)
                setIsSyncPending(false)
            }, SYNC_REFRESH_DELAY_MS)
        )
    }

    async function handleErpInvoiceSyncClick() {
        if (!reservationInvoice.linkErpInvoiceSync) {
            toastMessages.showSystemError()
            return
        }

        setIsSyncPending(true)

        try {
            await AssetService.syncErpInvoice(reservationInvoice.linkErpInvoiceSync)
        } catch (e) {
            setIsSyncPending(false)
            toastMessages.showSystemError()
            return
        }

        refreshInvoicesWithTimeout()
    }

    async function handleSendToErpClick() {
        setIsSendToErpPending(true)
        try {
            await AssetService.sendInvoicesToErp({
                invoiceIds: [reservationInvoice.id],
                headerText: reservationInvoice.headerText ?? null,
                footerText: reservationInvoice.footerText ?? null,
            })
        } catch (e) {
            setIsSendToErpPending(false)
            toastMessages.showSystemError()
        }

        refreshInvoicesWithTimeout()
    }

    async function handleCustomerNumberBlockedModalClose() {
        refreshInvoices()
        setShowCustomerNumberBlockedModal(false)
    }

    useEffect(() => {
        return () => {
            if (syncRefreshTimeoutId) {
                clearTimeout(syncRefreshTimeoutId)
            }
        }
    }, [syncRefreshTimeoutId])

    const contentListItems = useMemo<IListItem[]>(() => {
        const listItems: IListItem[] = [
            {
                label: t("reservation.customerNumber"),
                value: customer?.number
                    ? <Text bold>
                        <CustomerNumber
                            customerNumber={customer.number}
                            blocked={customer.blocked}
                            blockedTooltipText={t('common.customerNumberBlocked')}
                        />
                    </Text>
                    : <NoValue>{t('common.na')}</NoValue>,
            },
            {
                label: t("reservation.customerName"),
                value: customer?.name ? <Text className="white-space-normal" bold>{customer.name}</Text> : <NoValue />,
            },
            {
                label: t("invoiceDraft.costUnit"),
                value: costUnit ? <Text className="white-space-normal" bold>{costUnit}</Text> : <NoValue />,
            },
            ...isInvoiceERPInterfaceConnected ? [
                {
                    label: t("outgoingInvoices.orderNumber"),
                    value: <ErpOrderNumber
                        orderNumber={erpOrderNumber}
                        orderNumberStatus={erpOrderNumberStatus}
                        orderNumberMessage={erpOrderNumberMessage}
                        isInvoiceERPInterfaceConnected={isInvoiceERPInterfaceConnected}
                        longError
                        isItalic
                    />,
                },
                {
                    label: t("outgoingInvoices.invoiceNumber"),
                    value: <ErpInvoiceNumber
                        invoiceNumber={erpInvoiceNumber}
                        invoiceNumberStatus={erpInvoiceNumberStatus}
                        invoiceDate={erpInvoiceDate}
                        isInvoiceERPInterfaceConnected={isInvoiceERPInterfaceConnected}
                        longError
                        isItalic
                    />,
                },
                {
                    label: t("outgoingInvoices.invoiceDate"),
                    value: <ErpInvoiceDate
                        invoiceDate={erpInvoiceDate}
                        invoiceDateStatus={erpInvoiceDateStatus}
                        isInvoiceERPInterfaceConnected={isInvoiceERPInterfaceConnected}
                        longError
                        isItalic
                    />,
                },
                ...reservationInvoice.type === OutgoingInvoiceType.fullCreditNote
                    || reservationInvoice.type === OutgoingInvoiceType.collectiveCreditNote
                    || reservationInvoice.type === OutgoingInvoiceType.partialCreditNote
                    || reservationInvoice.type === OutgoingInvoiceType.partialDebitNote ? [
                    {
                        label: reservationInvoice.type === OutgoingInvoiceType.partialDebitNote
                            ? t("outgoingInvoices.debitedInvoice")
                            : t("outgoingInvoices.creditedInvoice"),
                        value: <Flex wrap="wrap" gap="0.4rem">
                            {parentInvoicesErp?.map(({ erpNumber, erpNumberStatus }, parentInvoicesErpIndex) => (
                                <FlexItem key={parentInvoicesErpIndex}>
                                    <ErpInvoiceNumber
                                        invoiceNumber={erpNumber}
                                        invoiceNumberStatus={erpNumberStatus}
                                        isInvoiceERPInterfaceConnected={isInvoiceERPInterfaceConnected}
                                        longError
                                        isItalic
                                    />{parentInvoicesErpIndex < parentInvoicesErp.length - 1 && ","}
                                </FlexItem>
                            ))}
                        </Flex>,
                    }
                ] : []
            ] : [],
        ]
        if (internalComment) {
            listItems.push({
                label: t('noteCreate.internalComments'),
                type: ListItemType.verticalSection,
                value: internalComment
            })
        }
        return listItems
    }, [costUnit, customer.blocked, customer.name, customer.number, erpInvoiceDate, erpInvoiceDateStatus, erpInvoiceNumber,
        erpInvoiceNumberStatus, erpOrderNumber, erpOrderNumberMessage, erpOrderNumberStatus, internalComment,
        isInvoiceERPInterfaceConnected, parentInvoicesErp, reservationInvoice.type, t])

    function renderHeader(toggle: () => void) {
        return (
            <Grid className="reservation-sidebar-invoice__header"
                columns="minmax(0, auto) max-content"
                alignItems="center">
                <button type="button"
                    className={cn(
                        "reservation-sidebar-invoice__title",
                        { "reservation-sidebar-invoice__title--selectable": onSelectionToggle },
                    )}
                    data-testid="reservation-sidebar-invoice-toggle-btn"
                    onClick={toggle}>
                    <Grid columns="minmax(0, auto) max-content">
                        <GridItem>
                            {!!onSelectionToggle &&
                                <div className="reservation-sidebar-invoice__checkbox" onClick={e => e.stopPropagation()}>
                                    <Checkbox
                                        checked={isSelected}
                                        onChange={onSelectionToggle}
                                    />
                                </div>
                            }
                            <Flex alignItems="center" gap="0.8rem">
                                <Text type="h5">
                                    {reservationInvoice.printPeriod()}
                                </Text>
                                {reservationInvoice.isAdHoc &&
                                    <FlexItem shrink={0}>
                                        <AdHocInvoiceIcon />
                                    </FlexItem>
                                }
                            </Flex>
                            {isInvoiceERPInterfaceConnected &&
                                <div className="mt-half">
                                    <ErpInvoiceNumber
                                        invoiceNumber={erpInvoiceNumber}
                                        invoiceNumberStatus={erpInvoiceNumberStatus}
                                        invoiceDate={erpInvoiceDate}
                                        isInvoiceERPInterfaceConnected={isInvoiceERPInterfaceConnected}
                                        isInvalid={!!erpOrderNumberMessage}
                                        longPending
                                        longError
                                        isItalic
                                    />
                                </div>
                            }
                        </GridItem>
                        <GridItem className="text-right">
                            {renderInvoiceAmount()}
                            {renderCreditedTag()}
                        </GridItem>
                    </Grid>
                </button>
                {hasPermission(UserPermission.OutgoingInvoicesCreate) &&
                    <div className="pr-3">
                        <OutgoingInvoiceControls
                            reservationId={reservationId}
                            invoiceId={reservationInvoice.id}
                            isSendToErpPending={isSendToErpPending}
                            isSyncPending={isSyncPending}
                            isAdHoc={reservationInvoice.isAdHoc}
                            onNoteCreated={onNoteCreated}
                            onSendToErpClick={isInvoiceERPInterfaceConnected && reservationInvoice.status === OutgoingInvoiceStatus.pendingErpSend
                                ? handleSendToErpClick
                                : undefined
                            }
                            onErpInvoiceSyncClick={reservationInvoice.linkErpInvoiceSync ? handleErpInvoiceSyncClick : undefined}
                            onCustomerBlocked={() => setShowCustomerNumberBlockedModal(true)}
                            onInvoiceBlocked={onInvoiceBlocked}
                            disabled={disabled}
                            onInvoiceResubmitted={onInvoiceResubmitted}
                            resubmitInvoiceLink={resubmitInvoiceLink}
                            isInvoicePrinted={!!reservationInvoice.erpInvoiceNumber || reservationInvoice.amount === 0.0}
                            onInvoiceChanged={refreshInvoices}
                        />
                    </div>
                }
            </Grid>
        )
    }

    return (
        <>
            <div className={cn([
                "reservation-sidebar-invoice",
                { "reservation-sidebar-invoice--expanded": expanded },
            ])}>
                <Spoiler
                    id={reservationInvoice.id}
                    expanded={expanded}
                    onToggle={setExpanded}
                    toggleEl={(_, toggle) => renderHeader(toggle)}>
                    <div className="reservation-sidebar-invoice__content">
                        {contentListItems.length > 0 &&
                            <div className="mb-4">
                                <List
                                    items={contentListItems}
                                />
                            </div>
                        }
                        <Text type="pSmallest" className="color-grey-500 text-right">
                            {createdBy
                                ? t("common.createdByOnX", { user: createdBy, date: reservationInvoice.created.format(t("config.dateLongTimeFormat")) })
                                : createdBy === ""
                                    ? t("common.createdOnX", { date: reservationInvoice.created.format(t("config.dateLongTimeFormat")) })
                                    : t("common.loading")
                            }
                        </Text>
                    </div>
                </Spoiler>
            </div>
            {showCustomerNumberBlockedModal &&
                <CustomerNumberBlockedModal
                    customerNumber={reservationInvoice.customer.number || ""}
                    onClose={handleCustomerNumberBlockedModalClose}
                />
            }
        </>
    )
}

export default ReservationSidebarInvoice
